import { BoxPage } from '@/components/common'
import { Card, Typography, CardContent, Divider, Tooltip, IconButton, Box, Grid, Button, Chip } from '@mui/material'
import { useParams } from 'react-router-dom'
import { RejectNoteBalanceDetail } from './RejectNoteBalanceDetail'
import { useNotification, useRouteQuery } from '@/hooks'
import { BalanceEditTransactionDetail } from './BalanceEditTransactionDetail'
import {
  ActivityLogTypeEnum,
  Balance,
  BalanceTransaction,
  BalanceTransactionStatusEnum,
  CompanyStatusEnum,
  getStatusConfig,
  ICompany,
  useBalanceTransactionApproveMutation,
  useBalanceTransactionDeleteMutation,
  useTransactionStatusOptions
} from '@/@data'
import { useQuery } from '@tanstack/react-query'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { EmployerStatusDropDown } from '@/components/employer'
import dayjs from 'dayjs'
import AppLogs from '@/components/common/AppLogs'
import { DialogConfirm } from '@/components/dialog/DialogConfirm'
import { getInitials } from '@/utils/getInitials'

export const BalanceTransactionDetail = () => {
  const { __, __n } = useTranslator()
  const { id, transactionId } = useParams()
  const { searchQuery } = useRouteQuery()
  const [open, setOpen] = useState(false)
  const [openEdit, setOpenEdit] = useState(false)
  const n = useNotification()
  const [isOpenLogs, setIsOpenLogs] = useState(false)
  const navigate = useNavigate()
  const [openConfirm, setOpenConfirm] = useState(false)

  const month = searchQuery.get('month') || new Date().toISOString().substring(0, 7)

  const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
    const { logo_file, name } = params

    if (logo_file) {
      return <CustomAvatar src={logo_file.tiny_url} skin='light' size={80} />
    } else {
      return (
        <CustomAvatar skin='light' size={80}>
          {getInitials(name as string)}
        </CustomAvatar>
      )
    }
  }

  const params = useMemo(() => {
    return {
      company_id: id,
      year: month.substring(0, 4),
      month: month.substring(5, 7)
    }
  }, [id, transactionId])

  const { data: dataBalanceTransactionDetail, refetch } = useQuery({
    ...BalanceTransaction.getBalanceTransactionById(Number(transactionId))
  })

  const { data: dataBalanceTransactionDept } = useQuery({
    ...Balance.getBalanceDeptByCompanyId(params)
  })

  const { mutate: approveBalanceTransactionDetail } = useBalanceTransactionApproveMutation()

  const handleApproveBalanceTransactionDetail = () => {
    approveBalanceTransactionDetail(
      { id: Number(transactionId) },
      {
        onSuccess: () => {
          n.success(__('Approve balance transaction successfully'))
          refetch()
        },
        onError: error => {
          n.error(error.message)
        }
      }
    )
  }

  const { mutate: deleteBalanceTransaction } = useBalanceTransactionDeleteMutation()

  const handleDeleteBalanceTransaction = () => {
    deleteBalanceTransaction(
      { id: Number(transactionId) },
      {
        onSuccess: () => {
          n.success(__('Delete balance transaction successfully'))
          refetch()
          navigate(`/cms/finance/balance/${id}?month=${month}`)
        },
        onError: error => {
          n.error(error.message)
        }
      }
    )
  }

  const handleDownload = async () => {
    if (dataBalanceTransactionDetail?.file?.url) {
      try {
        const response = await fetch(dataBalanceTransactionDetail.file.url)
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')

        link.href = url
        link.download = dataBalanceTransactionDetail?.file?.name || 'balance-transaction-file'
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        window.open(dataBalanceTransactionDetail.file.url, '_blank')
      }
    }
  }

  const config = dataBalanceTransactionDetail ? getStatusConfig(dataBalanceTransactionDetail.status) : null
  const statusOptions = useTransactionStatusOptions()
  const selectedOption = statusOptions.find(option => option.value === dataBalanceTransactionDetail?.status)

  const employerPercentage = Number(
    (
      (Number(dataBalanceTransactionDetail?.different ? dataBalanceTransactionDetail?.different : 0) /
        Number(
          dataBalanceTransactionDept?.data?.different?.reconciled
            ? dataBalanceTransactionDept?.data?.different?.reconciled
            : 0
        )) *
      100
    ).toFixed(2)
  )

  const liftPercentage = Number((100 - employerPercentage).toFixed(2))

  const wageDisbursementDifference = Number(dataBalanceTransactionDept?.data?.different?.reconciled)

  return (
    <>
      {openEdit && dataBalanceTransactionDetail ? (
        <BalanceEditTransactionDetail
          onClose={() => setOpenEdit(!openEdit)}
          dataBalanceTransactionDetail={dataBalanceTransactionDetail}
          dataBalanceTransactionDept={dataBalanceTransactionDept}
          refetch={refetch}
        />
      ) : (
        <BoxPage
          showBackButton={true}
          title={__('Balance Transaction Detail')}
          breadcrumbs={[
            { label: __('Finance Management'), href: `/cms/finance?month=${month}` },
            { label: __('Balance Management'), href: `/cms/finance/balance?month=${month}` },
            { label: __('Balance Company Detail'), href: `/cms/finance/balance/${id}?month=${month}` },
            { label: __('Balance Transaction Detail') }
          ]}
        >
          <Card className='rounded-[32px]'>
            <CardContent className='p-0'>
              <div className='flex justify-between items-center p-6'>
                <div className='flex flex-row gap-2 items-center'>
                  <Typography className='text-[24px] leading-[38px] font-medium'>
                    {dataBalanceTransactionDetail?.code}
                  </Typography>
                  <Button
                    variant='contained'
                    className={`text-[${config?.textColor}] shadow-none`}
                    sx={{
                      borderRadius: 10,
                      height: '30px',
                      fontSize: '13px',
                      backgroundColor: config?.backgroundColor
                    }}
                  >
                    {selectedOption?.label}
                  </Button>
                </div>
                <div className='flex flex-row gap-6 items-center'>
                  <Typography className='text-[15px] leading-[22px] font-normal'>
                    {__('Creator')}: {dataBalanceTransactionDetail?.created_user?.first_name || '-'}
                  </Typography>
                  <Typography className='text-[15px] leading-[22px] font-normal'>
                    {__('Create Date')}:{dayjs(dataBalanceTransactionDetail?.created_at).format('DD/MM/YYYY')}
                  </Typography>
                  <Typography className='text-[15px] leading-[22px] font-normal'>
                    {__('Date Input Pay')}:{' '}
                    {dataBalanceTransactionDetail?.approve_at
                      ? dayjs(dataBalanceTransactionDetail?.approve_at).format('DD/MM/YYYY')
                      : '-'}
                  </Typography>
                  <Tooltip title='History'>
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#DEE3FD'
                      }}
                      onClick={() => setIsOpenLogs(true)}
                    >
                      <i className='tabler-history text-[#3551F2]' style={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <Divider />

              <Box className='p-6'>
                <Grid container spacing={6}>
                  <Grid size={{ xs: 12, md: 8.5 }}>
                    <Box className='flex flex-row gap-2 items-center w-full bg-[#D4F4E2] rounded-[24px] p-4 h-[128px]'>
                      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                        {getAvatar({
                          logo_file: dataBalanceTransactionDetail?.company?.logo_file,
                          name: dataBalanceTransactionDetail?.company?.name || '-'
                        })}
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Typography
                              variant='h5'
                              gutterBottom
                              sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}
                              className='truncate max-w-[300px]'
                              title={dataBalanceTransactionDetail?.company.name}
                            >
                              {dataBalanceTransactionDetail?.company.name}
                            </Typography>
                            <EmployerStatusDropDown
                              status={dataBalanceTransactionDetail?.company?.status as CompanyStatusEnum}
                              isDetail={true}
                              sx={{
                                height: '30px',
                                fontSize: '13px'
                              }}
                            />
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <i className='tabler-id' />
                            <span className='text-[15px] leading-[22px]'>
                              {dataBalanceTransactionDetail?.company.system_code}
                            </span>

                            <i
                              className='tabler-copy'
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                navigator.clipboard.writeText(dataBalanceTransactionDetail?.company.system_code || '')
                                n.success(__('Copied to clipboard!'))
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3.5 }}>
                    <Box className='bg-[#3D5AFE] rounded-[24px] p-6 h-[128px]  relative overflow-hidden flex  flex-col gap-2 '>
                      <Typography className='text-[20px] leading-[32px] font-medium text-white'>
                        {dataBalanceTransactionDetail?.company?.bank?.shortName}
                      </Typography>
                      <Typography className='text-[15px] leading-[22px] font-normal text-white'>
                        {dataBalanceTransactionDetail?.company.bank_account_number}
                      </Typography>
                      <Box className='absolute -bottom-20 -right-20 w-40 h-40 bg-[#6E83FE] rounded-full opacity-80'></Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Box className='px-6 pb-6'>
                <Grid container spacing={8}>
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Box className='flex flex-col gap-8'>
                      <div className='flex flex-col gap-2'>
                        <Typography variant='h6' className='font-semibold mb-2'>
                          {__('Transaction Detail')}
                        </Typography>
                        <Typography variant='body2'>
                          {__('Review your transaction details – Confirm to complete payment securely.')}
                        </Typography>
                      </div>
                      <div className='overflow-x-auto border rounded-[6px]'>
                        <table className='w-full border-collapse'>
                          <thead>
                            <tr className='border-b'>
                              <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                                #
                              </th>
                              <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                                {__('TYPE')}
                              </th>
                              <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                                {__('EWA DISBURSEMENT')}
                              </th>
                              <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                                {__('RECONCILED')}
                              </th>
                              <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                                {__('RECEIVE')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className='border-b border-gray-200 '>
                              <td className='p-6 text-[15px] font-normal leading-[22px] '>1</td>
                              <td className='p-6 text-[15px] font-normal leading-[22px] '>{__('Voucher')}</td>
                              <td className='px-6 py-4 text-left text-[15px] font-normal leading-[22px]'>
                                {__n(dataBalanceTransactionDept?.data?.voucher_no_tax?.ewa_disbursement, 'đ')}
                              </td>
                              <td className='px-6 py-4 text-left text-[15px] font-normal leading-[22px]'>
                                {__n(dataBalanceTransactionDept?.data?.voucher_no_tax?.reconciled, 'đ')}
                              </td>
                              <td className='px-6 py-4'>{__n(dataBalanceTransactionDetail?.voucher, 'đ')}</td>
                            </tr>
                            <tr className='border-b border-gray-200 '>
                              <td className='p-6 text-[15px] font-normal leading-[22px] '>2</td>
                              <td className='p-6 text-[15px] font-normal leading-[22px] '>{__('Voucher Tax')}</td>
                              <td className='px-6 py-4 text-left text-[15px] font-normal leading-[22px]'>
                                {__n(dataBalanceTransactionDept?.data?.voucher_tax?.ewa_disbursement, 'đ')}
                              </td>
                              <td className='px-6 py-4 text-left text-[15px] font-normal leading-[22px]'>
                                {__n(dataBalanceTransactionDept?.data?.voucher_tax?.reconciled, 'đ')}
                              </td>
                              <td className='px-6 py-4'>{__n(dataBalanceTransactionDetail?.voucher_tax, 'đ')}</td>
                            </tr>
                            <tr className='border-gray-200 border-b'>
                              <td className='px-6 py-4 text-[15px] font-normal leading-[22px] '>3</td>
                              <td className='px-6 py-4 text-[15px] font-normal leading-[22px] w-[180px]'>
                                {__('Return Wages Disbursement')}
                              </td>

                              <td className='p-6 text-left text-[15px] font-normal leading-[22px]'>
                                {__n(
                                  dataBalanceTransactionDept?.data?.return_wages_disbursement?.ewa_disbursement,
                                  'đ'
                                )}
                              </td>
                              <td className='p-6 text-left text-[15px] font-normal leading-[22px]'>
                                {__n(dataBalanceTransactionDept?.data?.return_wages_disbursement?.reconciled, 'đ')}
                              </td>
                              <td className='p-6'>{__n(dataBalanceTransactionDetail?.debt_disbursement, 'đ')}</td>
                            </tr>

                            {wageDisbursementDifference > 0 && (
                              <tr className=' border-gray-200'>
                                <td className='px-6 py-4 text-[15px] font-normal leading-[22px] '>4</td>
                                <td className='px-6 py-4 text-[15px] font-normal leading-[22px]'>{__('Different')}</td>
                                <td className='p-6 text-[15px] font-normal leading-[22px] flex gap-4 flex-col items-start'>
                                  <Chip
                                    label={__(`Employer ${employerPercentage}% `)}
                                    className={`bg-[#D6F4F7] rounded-full ${employerPercentage > 0 ? 'flex' : 'hidden'}  text-[#00BAD1] text-[13px] font-medium leading-[18px]`}
                                  />
                                  <Chip
                                    label={__(`Lift ${liftPercentage}% `)}
                                    className={`bg-[#DEE3FD] rounded-full ${liftPercentage > 0 ? 'flex' : 'hidden'} text-[#3551F2] text-[13px] font-medium leading-[18px] `}
                                  />
                                </td>
                                <td className='p-6 text-left text-[15px] font-normal leading-[22px]'>
                                  {__n(dataBalanceTransactionDept?.data?.different?.reconciled, 'đ')}
                                </td>
                                <td className='p-6'>{__n(dataBalanceTransactionDetail?.different, 'đ')}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Box>
                  </Grid>
                  <Divider orientation='vertical' flexItem />
                  <Grid size={{ xs: 12, md: 4.5 }}>
                    <Box className='flex flex-col gap-8 '>
                      <div className='flex flex-col gap-2'>
                        <Typography variant='h6' className='font-semibold mb-2'>
                          {__('Transaction Summary')}
                        </Typography>
                        <Typography variant='body2'>
                          {__('Payment summary - Final check before confirmation.')}
                        </Typography>
                      </div>
                      <Box className='flex flex-col gap-4 rounded-[6px] bg-[#F3F2F3] p-6'>
                        <Typography variant='h6' className='font-semibold mb-2'>
                          {__(`Total: 1 + 2 + 3 ${wageDisbursementDifference > 0 ? ' + 4' : ''}`)}
                        </Typography>
                        <Typography className='text-[46px] font-medium leading-[68px]'>
                          {__n(
                            Number(dataBalanceTransactionDetail?.voucher || 0) +
                              Number(dataBalanceTransactionDetail?.voucher_tax || 0) +
                              Number(dataBalanceTransactionDetail?.debt_disbursement || 0) +
                              Number(dataBalanceTransactionDetail?.different || 0),
                            'đ'
                          )}
                        </Typography>
                      </Box>
                      {dataBalanceTransactionDetail?.status === BalanceTransactionStatusEnum.Pending && (
                        <Box className='flex flex-col gap-5'>
                          <div className='flex justify-between gap-5'>
                            <Button
                              variant='contained'
                              className='text-white text-[15px] font-medium leading-[22px] bg-[#49C76E] shadow-none rounded-full w-full'
                              endIcon={<i className='tabler-check text-white' style={{ fontSize: 18 }} />}
                              onClick={handleApproveBalanceTransactionDetail}
                            >
                              {__('Approve')}
                            </Button>
                            <Button
                              variant='contained'
                              className='text-white text-[15px] font-medium leading-[22px] bg-[#F24C52] shadow-none rounded-full w-full'
                              endIcon={<i className='tabler-x text-white' style={{ fontSize: 18 }} />}
                              onClick={() => setOpen(!open)}
                            >
                              {__('Reject')}
                            </Button>
                          </div>
                          <div className='flex justify-between gap-5'>
                            <Button
                              variant='contained'
                              className='text-white text-[15px] font-medium leading-[22px] bg-[#3551F2] shadow-none rounded-full w-full'
                              onClick={() => setOpenEdit(true)}
                            >
                              {__('Edit')}
                            </Button>
                            <Button
                              variant='contained'
                              className='text-[#6F83F6] text-[15px] font-medium leading-[22px] bg-[#E8EBFD] shadow-none rounded-full w-full'
                              onClick={() => setOpenConfirm(true)}
                            >
                              {__('Delete')}
                            </Button>
                          </div>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
                <Divider className='my-6 border-dashed' />
                <div className='flex flex-row gap-2 items-center'>
                  <Typography variant='h6' className='font-semibold '>
                    {__('File')}:
                  </Typography>
                  <div className='flex items-center cursor-pointer' onClick={handleDownload}>
                    <Typography className='text-[#3551F2]'>{dataBalanceTransactionDetail?.file?.name}</Typography>
                  </div>
                  <div
                    onClick={() => window.open(dataBalanceTransactionDetail?.file?.url, '_blank')}
                    className='flex items-center cursor-pointer'
                  >
                    <i className='tabler-eye' style={{ fontSize: 18 }} />
                  </div>
                </div>
              </Box>
            </CardContent>
          </Card>
          {dataBalanceTransactionDetail && (
            <RejectNoteBalanceDetail
              open={open}
              setOpen={setOpen}
              dataBalanceTransactionDetail={dataBalanceTransactionDetail}
              id={id || ''}
              month={month}
            />
          )}
          <AppLogs
            open={isOpenLogs}
            setOpen={setIsOpenLogs}
            title={__('History Menu Balance Transaction Detail')}
            object_type={ActivityLogTypeEnum.Finance}
            objectId={transactionId || ''}
          />
          <DialogConfirm
            open={openConfirm}
            onClose={() => setOpenConfirm(false)}
            onConfirm={handleDeleteBalanceTransaction}
            title={'Confirm Deletion'}
            content={'Are you sure you want to delete this balance transaction? This action cannot be undone.'}
          />
        </BoxPage>
      )}
    </>
  )
}
